import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { useReportFilters } from "../shared/useReportFilters";
import { toISO } from "../shared/utils";
import { API_URL } from "../shared/constants";

export function useQualityReport() {
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(
    async (c) => {
      if (!c) return;
      setLoading(true);
      try {
        const p = new URLSearchParams();
        p.set("startDate", c.startDate);
        p.set("endDate", c.endDate);
        p.set("page", "1");
        p.set("limit", "500");
        if (c.machineIds.length > 0)
          p.set("machineIds", c.machineIds.join(","));
        if (c.shiftId) p.set("shiftId", c.shiftId);
        const r = await axios.get(
          `${API_URL}/api/scrap-entry/daily-rows?${p}`,
          authH(),
        );
        setRows(r.data.rows || []);
      } catch {
        addToast("Kalite verisi yüklenemedi", "danger");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [authH, addToast],
  );

  const handleApply = useCallback(() => {
    fetchData({
      startDate: toISO(filters.startDate, false),
      endDate: toISO(filters.endDate, true),
      machineIds: [...filters.machineIds],
      shiftId: filters.shiftId || "",
    });
  }, [filters, fetchData]);

  useEffect(() => {
    handleApply();
  }, []); // eslint-disable-line

  const derived = useMemo(() => {
    if (!rows.length) return null;

    const dateMap = {};
    rows.forEach((r) => {
      if (!dateMap[r.date])
        dateMap[r.date] = { produced: 0, rejected: 0, reworked: 0 };
      dateMap[r.date].produced += r.totalProduced || 0;
      if (r.scrapEntry) {
        dateMap[r.date].rejected += r.scrapEntry.rejected || 0;
        dateMap[r.date].reworked += r.scrapEntry.reworked || 0;
      }
    });
    const dateKeys = Object.keys(dateMap).sort();

    let totalProduced = 0;
    let totalRejected = 0;
    dateKeys.forEach((d) => {
      const day = dateMap[d];
      day.refusePct =
        day.produced > 0
          ? Math.round((day.rejected / day.produced) * 1000) / 10
          : 0;
      totalProduced += day.produced;
      totalRejected += day.rejected;
    });
    const summary = {
      produced: totalProduced,
      rejected: totalRejected,
      refusePct:
        totalProduced > 0
          ? Math.round((totalRejected / totalProduced) * 1000) / 10
          : 0,
    };

    const partMap = {};
    rows.forEach((r) => {
      const pk = r.partCode || r.partKey || "—";
      const pn = r.partName || r.partCode || "Bilinmeyen Parça";
      if (!partMap[pk]) partMap[pk] = { name: pn, produced: 0, rejected: 0 };
      partMap[pk].produced += r.totalProduced || 0;
      if (r.scrapEntry) partMap[pk].rejected += r.scrapEntry.rejected || 0;
    });
    const partArr = Object.entries(partMap)
      .map(([code, v]) => ({
        code,
        name: v.name,
        produced: v.produced,
        rejected: v.rejected,
        refusePct:
          v.produced > 0
            ? Math.round((v.rejected / v.produced) * 1000) / 10
            : 0,
      }))
      .filter((p) => p.produced > 0)
      .sort((a, b) => b.refusePct - a.refusePct)
      .slice(0, 15);

    return { dateMap, dateKeys, partArr, summary };
  }, [rows]);

  return {
    filters,
    machines,
    shifts,
    rows,
    loading,
    derived,
    handleApply,
    toasts,
    dismissToast,
  };
}
